/**
 * Semantic defaults for appointments whose clinical activity is already known.
 *
 * A caller should pass a preset key instead of rebuilding an appointment from
 * a display label. Dates and times are intentionally not part of a preset:
 * they belong to the scheduling context and stay blank until that context
 * knows them.
 */

export type ClinicalAppointmentPresetKey =
  | "beta_hcg"
  | "hormonal_testing"
  | "std_testing"
  | "thyroid_testing"
  | "urinalysis"
  | "semen_analysis"
  | "swab_test"
  | "pap_smear"
  | "transabdominal_ultrasound"
  | "transvaginal_ultrasound"
  | "doppler_ultrasound"
  | "hysterosonography"
  | "hsg"
  | "antral_follicle_count"
  | "pelvic_mri"
  | "karyotyping"
  | "carrier_testing"
  | "exome_sequencing"
  | "egg_retrieval"
  | "sperm_collection"
  | "surgical_sperm_retrieval"
  | "embryo_transfer"
  | "insemination"
  | "laparoscopy"
  | "hysteroscopy"
  | "endometrial_biopsy"
  | "testicular_biopsy";

export type ClinicalAppointmentType = "exam" | "procedure";

export type ClinicalAppointmentPreset = {
  key: ClinicalAppointmentPresetKey;
  type: ClinicalAppointmentType;
  activity: string;
  title: string;
  defaults: {
    /** A lab collection does not need the generic one-hour appointment. */
    durationMinutes?: number;
    /** Known exams and procedures are physical visits by default. */
    isOnline: false;
    /** A generic destination is editable and is not a clinic identity. */
    location?: string;
  };
};

const LAB_DEFAULTS = {
  durationMinutes: 30,
  isOnline: false,
  location: "Lab",
} as const;

const IN_PERSON_DEFAULTS = {
  isOnline: false,
} as const;

export const CLINICAL_APPOINTMENT_PRESETS: Record<
  ClinicalAppointmentPresetKey,
  ClinicalAppointmentPreset
> = {
  beta_hcg: {
    key: "beta_hcg",
    type: "exam",
    activity: "Beta hCG",
    title: "Beta hCG",
    defaults: LAB_DEFAULTS,
  },
  hormonal_testing: {
    key: "hormonal_testing",
    type: "exam",
    activity: "Hormonal testing",
    title: "Hormonal testing",
    defaults: LAB_DEFAULTS,
  },
  std_testing: {
    key: "std_testing",
    type: "exam",
    activity: "STD testing",
    title: "STD testing",
    defaults: LAB_DEFAULTS,
  },
  thyroid_testing: {
    key: "thyroid_testing",
    type: "exam",
    activity: "Thyroid testing",
    title: "Thyroid testing",
    defaults: LAB_DEFAULTS,
  },
  urinalysis: {
    key: "urinalysis",
    type: "exam",
    activity: "Urinalysis",
    title: "Urinalysis",
    defaults: LAB_DEFAULTS,
  },
  semen_analysis: {
    key: "semen_analysis",
    type: "exam",
    activity: "Semen analysis",
    title: "Semen analysis",
    defaults: LAB_DEFAULTS,
  },
  swab_test: {
    key: "swab_test",
    type: "exam",
    activity: "Swab test",
    title: "Swab test",
    defaults: LAB_DEFAULTS,
  },
  pap_smear: {
    key: "pap_smear",
    type: "exam",
    activity: "Pap smear",
    title: "Pap smear",
    defaults: IN_PERSON_DEFAULTS,
  },
  transabdominal_ultrasound: {
    key: "transabdominal_ultrasound",
    type: "exam",
    activity: "Transabdominal ultrasound",
    title: "Transabdominal ultrasound",
    defaults: IN_PERSON_DEFAULTS,
  },
  transvaginal_ultrasound: {
    key: "transvaginal_ultrasound",
    type: "exam",
    activity: "Transvaginal ultrasound",
    title: "Transvaginal ultrasound",
    defaults: IN_PERSON_DEFAULTS,
  },
  doppler_ultrasound: {
    key: "doppler_ultrasound",
    type: "exam",
    activity: "Doppler ultrasound",
    title: "Doppler ultrasound",
    defaults: IN_PERSON_DEFAULTS,
  },
  hysterosonography: {
    key: "hysterosonography",
    type: "exam",
    activity: "Hysterosonography",
    title: "Hysterosonography",
    defaults: IN_PERSON_DEFAULTS,
  },
  hsg: {
    key: "hsg",
    type: "exam",
    activity: "HSG (Hysterosalpingogram)",
    title: "HSG (Hysterosalpingogram)",
    defaults: IN_PERSON_DEFAULTS,
  },
  antral_follicle_count: {
    key: "antral_follicle_count",
    type: "exam",
    activity: "Antral follicle count",
    title: "Antral follicle count",
    defaults: IN_PERSON_DEFAULTS,
  },
  pelvic_mri: {
    key: "pelvic_mri",
    type: "exam",
    activity: "Pelvic MRI",
    title: "Pelvic MRI",
    defaults: IN_PERSON_DEFAULTS,
  },
  karyotyping: {
    key: "karyotyping",
    type: "exam",
    activity: "Karyotyping",
    title: "Karyotyping",
    defaults: LAB_DEFAULTS,
  },
  carrier_testing: {
    key: "carrier_testing",
    type: "exam",
    activity: "Carrier testing",
    title: "Carrier testing",
    defaults: LAB_DEFAULTS,
  },
  exome_sequencing: {
    key: "exome_sequencing",
    type: "exam",
    activity: "Exome or genome sequencing",
    title: "Exome or genome sequencing",
    defaults: LAB_DEFAULTS,
  },
  egg_retrieval: {
    key: "egg_retrieval",
    type: "procedure",
    activity: "Egg retrieval",
    title: "Egg retrieval",
    defaults: IN_PERSON_DEFAULTS,
  },
  sperm_collection: {
    key: "sperm_collection",
    type: "procedure",
    activity: "Sperm collection",
    title: "Sperm collection",
    defaults: IN_PERSON_DEFAULTS,
  },
  surgical_sperm_retrieval: {
    key: "surgical_sperm_retrieval",
    type: "procedure",
    activity: "Surgical sperm retrieval",
    title: "Surgical sperm retrieval",
    defaults: IN_PERSON_DEFAULTS,
  },
  embryo_transfer: {
    key: "embryo_transfer",
    type: "procedure",
    activity: "Embryo transfer",
    title: "Embryo transfer",
    defaults: IN_PERSON_DEFAULTS,
  },
  insemination: {
    key: "insemination",
    type: "procedure",
    activity: "Intrauterine insemination (IUI)",
    title: "IUI",
    defaults: IN_PERSON_DEFAULTS,
  },
  laparoscopy: {
    key: "laparoscopy",
    type: "procedure",
    activity: "Laparoscopy",
    title: "Laparoscopy",
    defaults: IN_PERSON_DEFAULTS,
  },
  hysteroscopy: {
    key: "hysteroscopy",
    type: "procedure",
    activity: "Hysteroscopy",
    title: "Hysteroscopy",
    defaults: IN_PERSON_DEFAULTS,
  },
  endometrial_biopsy: {
    key: "endometrial_biopsy",
    type: "procedure",
    activity: "Endometrial biopsy",
    title: "Endometrial biopsy",
    defaults: IN_PERSON_DEFAULTS,
  },
  testicular_biopsy: {
    key: "testicular_biopsy",
    type: "procedure",
    activity: "Testicular biopsy",
    title: "Testicular biopsy",
    defaults: IN_PERSON_DEFAULTS,
  },
};

export type ClinicalProcedurePresetKey = Extract<
  ClinicalAppointmentPresetKey,
  | "egg_retrieval"
  | "sperm_collection"
  | "surgical_sperm_retrieval"
  | "embryo_transfer"
  | "insemination"
  | "laparoscopy"
  | "hysteroscopy"
  | "endometrial_biopsy"
  | "testicular_biopsy"
>;

export type ClinicalAppointmentPrefill = {
  clinicalPreset: ClinicalAppointmentPresetKey;
  type: ClinicalAppointmentType;
  activities: string[];
  title: string;
  date?: string;
  durationMinutes?: number;
  isOnline?: boolean;
  location?: string;
  notes?: string;
  cycleId?: string | null;
};

export function getClinicalAppointmentPreset(
  key: ClinicalAppointmentPresetKey,
): ClinicalAppointmentPreset {
  return CLINICAL_APPOINTMENT_PRESETS[key];
}

export function buildClinicalAppointmentPrefill(
  key: ClinicalAppointmentPresetKey,
  overrides: Omit<Partial<ClinicalAppointmentPrefill>, "clinicalPreset"> = {},
): ClinicalAppointmentPrefill {
  const preset = getClinicalAppointmentPreset(key);
  return {
    clinicalPreset: key,
    type: preset.type,
    activities: [preset.activity],
    title: preset.title,
    ...preset.defaults,
    ...overrides,
  };
}
